import { Link } from "@tanstack/react-router";
import type { NetworkMerchant } from "@unbox/api";
import {
  Announcement03,
  Award03,
  Colors,
  File06,
  Fingerprint04,
  Globe05,
  Heart,
  ImageIndentLeft,
  IntersectCircle,
  Share03,
  UserCircle,
  Users01,
} from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { NetworkSection } from "@/components/creator-network/creator-network";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useMerchant } from "@/providers/MerchantProvider";
import { creatorExplorerRoute, openCollaborationDetailRoute, targetCollaborationDetailRoute } from "@/router";
import { NoInsightsEmptyState } from "../no-insights-empty-state";

type CollaborationBadgeColor = "success" | "brand" | "warning" | "gray" | "error";

const resolveCollaborationBadge = (collab: {
  kind?: "target" | "open" | "invitation" | null;
  status?: string | null;
  membershipStatus?: string | null;
}): { label: string; color: CollaborationBadgeColor } => {
  if (collab.kind === "invitation") {
    return { label: "Invited", color: "warning" };
  }
  if (collab.membershipStatus === "removed") {
    return { label: "Removed from plan", color: "error" };
  }
  if (collab.status && collab.status !== "NORMAL") {
    return { label: "Plan Inactive", color: "gray" };
  }
  if (collab.kind === "open") {
    return { label: "In Open plan", color: "brand" };
  }
  return { label: "In Target plan", color: "success" };
};

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

type AboutTabProps = {
  network: NetworkMerchant[];
  data: {
    creatorName: string;
    contentAnalysis?: {
      topTactics?: string[];
      topArchetypes?: string[];
    } | null;
    aiFeatures?: {
      contentFocus: string[];
      visualAesthetic: string[];
      toneAndApproach: string[];
      productionQuality: string[];
      descriptiveSummaryOfCreator: string;
    } | null;
    targetCollaborations?: Array<{
      collaborationId: string | number;
      name: string;
      kind?: "target" | "open" | "invitation" | null;
      status?: string | null;
      membershipStatus?: string | null;
      type?: string | null;
    }>;
    lists?: Array<{
      id: string | number;
      name: string;
      description?: string | null;
    }>;
    bioDescription?: string | null;
    categories?: string[];
    externalUrls?: string[];
    profilePage?: string | null;
    demographics?: {
      gender: { male?: number; female?: number } | null;
      ageDistribution: Record<string, number> | null;
    } | null;
    socialStats?: {
      followers: number;
      avgLikes: number;
    } | null;
  };
};

export const AboutTab = ({ data, network }: AboutTabProps) => {
  const merchantId = useMerchant();

  // Process demographics data
  const getMajorGender = () => {
    if (!data.demographics?.gender) return null;
    const { male, female } = data.demographics.gender;
    if (!male && !female) return null;
    const malePercent = male || 0;
    const femalePercent = female || 0;
    if (malePercent > femalePercent) {
      return { type: "Male", percentage: Math.round(malePercent * 100) };
    }
    return { type: "Female", percentage: Math.round(femalePercent * 100) };
  };

  const getMajorAgeGroup = () => {
    if (!data.demographics?.ageDistribution) return null;
    const ageDistribution = data.demographics.ageDistribution;
    let maxPercent = 0;
    let maxRange = "";
    for (const [range, percent] of Object.entries(ageDistribution)) {
      if (percent > maxPercent) {
        maxPercent = percent;
        maxRange = range;
      }
    }
    return maxRange ? { range: maxRange, percentage: Math.round(maxPercent * 100) } : null;
  };

  // Use real data when available
  const displayData = {
    bio: data.bioDescription,
    demographics: {
      gender: getMajorGender(),
      ageGroup: getMajorAgeGroup(),
    },
    socialStats: {
      followers: data.socialStats?.followers || 0,
      avgLikes: data.socialStats?.avgLikes || 0,
    },
    topSellingCategories: data.categories || [],
    links: {
      tiktokProfile: data.profilePage || `https://www.tiktok.com/@${data.creatorName.toLowerCase().replace(/\s/g, "")}`,
      externalWebsite: data.externalUrls?.[0] || null,
    },
  };

  // Check if creator insights are available
  const hasCreatorInsights =
    displayData.bio ||
    (data.contentAnalysis?.topTactics && data.contentAnalysis.topTactics.length > 0) ||
    (data.contentAnalysis?.topArchetypes && data.contentAnalysis.topArchetypes.length > 0);

  return (
    <div className="flex gap-x-8 gap-y-8">
      {/* Left Column */}
      <div className="w-[65%]">
        {/* Creator insights section */}
        {hasCreatorInsights ? (
          <>
            {/* Creator Summary */}
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UserCircle className="w-4 h-4 text-tertiary" />
                  <h3 className="text-sm font-medium text-secondary">Descriptive Summary of Creator</h3>
                </div>
                <div className="h-[1px] bg-border-primary" />
              </div>
              {displayData.bio ? (
                <p className="text-sm text-tertiary leading-relaxed">{displayData.bio}</p>
              ) : (
                <p className="text-sm text-tertiary italic">No bio available</p>
              )}
            </div>

            {/* Tone & Approach - REAL DATA */}
            <div className="space-y-3 mt-15">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Announcement03 className="w-4 h-4 text-tertiary" />
                  <h3 className="text-sm font-medium text-secondary">Tone & Approach</h3>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {data.aiFeatures?.toneAndApproach && data.aiFeatures.toneAndApproach.length > 0 ? (
                  data.aiFeatures.toneAndApproach.map((tone) => (
                    <Badge key={tone} color="gray" className="w-fit">
                      {tone}
                    </Badge>
                  ))
                ) : data.contentAnalysis?.topTactics && data.contentAnalysis.topTactics.length > 0 ? (
                  data.contentAnalysis.topTactics.map((tone) => (
                    <Badge key={tone} color="gray" className="w-fit">
                      {tone}
                    </Badge>
                  ))
                ) : (
                  <div className="text-sm text-tertiary">No data available</div>
                )}
              </div>
            </div>

            {/* Content Focus - REAL DATA */}
            <div className="space-y-3 mt-15">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <ImageIndentLeft className="w-4 h-4 text-tertiary" />
                  <h3 className="text-sm font-medium text-secondary">Content Focus</h3>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {data.aiFeatures?.contentFocus && data.aiFeatures.contentFocus.length > 0 ? (
                  data.aiFeatures.contentFocus.map((focus) => (
                    <Badge key={focus} color="gray" className="w-fit">
                      {focus}
                    </Badge>
                  ))
                ) : data.contentAnalysis?.topArchetypes && data.contentAnalysis.topArchetypes.length > 0 ? (
                  data.contentAnalysis.topArchetypes.map((focus) => (
                    <Badge key={focus} color="gray" className="w-fit">
                      {focus}
                    </Badge>
                  ))
                ) : (
                  <div className="text-sm text-tertiary">No data available</div>
                )}
              </div>
            </div>

            {/* Production Quality */}
            <div className="space-y-3 mt-15">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Award03 className="w-4 h-4 text-tertiary" />
                  <h3 className="text-sm font-medium text-secondary">Production Quality</h3>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {data.aiFeatures?.productionQuality && data.aiFeatures.productionQuality.length > 0 ? (
                  data.aiFeatures.productionQuality.map((quality) => (
                    <Badge key={quality} color="gray" className="w-fit">
                      {quality}
                    </Badge>
                  ))
                ) : (
                  <div className="text-sm text-tertiary">No data available</div>
                )}
              </div>
            </div>

            {/* Visual Aesthetic */}
            <div className="space-y-3 mt-15">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Fingerprint04 className="w-4 h-4 text-tertiary" />
                  <h3 className="text-sm font-medium text-secondary">Visual aesthetic</h3>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {data.aiFeatures?.visualAesthetic && data.aiFeatures.visualAesthetic.length > 0 ? (
                  data.aiFeatures.visualAesthetic.map((aesthetic) => (
                    <Badge key={aesthetic} color="gray" className="w-fit">
                      {aesthetic}
                    </Badge>
                  ))
                ) : (
                  <div className="text-sm text-tertiary">No data available</div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <UserCircle className="w-4 h-4 text-tertiary" />
                <h3 className="text-sm font-medium text-secondary">Creator insights</h3>
              </div>
              <div className="h-[1px] bg-border-primary" />
            </div>
            <NoInsightsEmptyState
              backgroundImage="/creator-about-empty-state.png"
              align="center"
              className="min-h-[34rem] mt-3"
            />
          </>
        )}

        {/* Their Collaborations - REAL DATA */}
        <div className="space-y-3 mt-15">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <IntersectCircle className="w-4 h-4 text-tertiary" />
              <h3 className="text-sm font-medium text-secondary">Their Collaborations</h3>
            </div>
            <div className="h-[1px] bg-border-primary" />
          </div>
          <div className="space-y-2">
            {data.targetCollaborations && data.targetCollaborations.length > 0 ? (
              data.targetCollaborations.map((collab) => {
                const badge = resolveCollaborationBadge(collab);
                const key = `${collab.kind ?? "target"}-${String(collab.collaborationId)}`;
                const linkClassName =
                  "text-sm text-primary underline decoration-dotted hover:text-brand-primary truncate min-w-0 flex-1";
                const nameNode =
                  merchantId && collab.kind === "target" ? (
                    <Link
                      to={targetCollaborationDetailRoute.to}
                      params={{ merchantId, collaborationId: String(collab.collaborationId) }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClassName}
                    >
                      {collab.name}
                    </Link>
                  ) : merchantId && collab.kind === "open" ? (
                    <Link
                      to={openCollaborationDetailRoute.to}
                      params={{ merchantId, collaborationId: String(collab.collaborationId) }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClassName}
                    >
                      {collab.name}
                    </Link>
                  ) : (
                    <span className="text-sm text-primary truncate min-w-0 flex-1">{collab.name}</span>
                  );
                return (
                  <div key={key} className="flex items-center justify-between gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>{nameNode}</TooltipTrigger>
                      <TooltipContent side="top" className="max-w-80 break-words">
                        {collab.name}
                      </TooltipContent>
                    </Tooltip>
                    <Badge color={badge.color} size="sm" className="shrink-0">
                      {badge.label}
                    </Badge>
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-tertiary">No collaborations yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="w-[35%]">
        <NetworkSection network={network} className="mb-15 block" />

        {/* Top 3 Selling Categories */}
        <div className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Colors className="w-4 h-4 text-tertiary" />
              <h3 className="text-sm font-medium text-secondary">Top 3 selling categories</h3>
            </div>
            <div className="h-[1px] bg-border-primary" />
          </div>
          <div className="flex flex-col gap-2">
            {displayData.topSellingCategories.length > 0 ? (
              displayData.topSellingCategories.slice(0, 3).map((category) =>
                merchantId ? (
                  <Link
                    key={category}
                    to={creatorExplorerRoute.to}
                    params={{ merchantId }}
                    search={{
                      topSellingCategories: [{ id: category, label: category }],
                      classification: "__all",
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-fit"
                    aria-label={`Browse creators in ${category}`}
                  >
                    <Badge color="gray" className="cursor-pointer hover:ring-2 hover:ring-brand-primary transition-all">
                      {category}
                    </Badge>
                  </Link>
                ) : (
                  <Badge key={category} color="gray" className="w-fit">
                    {category}
                  </Badge>
                ),
              )
            ) : (
              <div className="text-sm text-tertiary italic">No categories available</div>
            )}
          </div>
        </div>

        {/* Demographics */}
        <div className="space-y-3 mt-15">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Globe05 className="w-4 h-4 text-tertiary" />
              <h3 className="text-sm font-medium text-secondary">Demographics</h3>
            </div>
            <div className="h-[1px] bg-border-primary" />
          </div>
          {displayData.demographics.gender || displayData.demographics.ageGroup ? (
            <div className="space-y-0">
              {displayData.demographics.gender && (
                <div className="flex justify-between items-center py-3 border-b border-border-primary">
                  <span className="text-sm text-primary">{displayData.demographics.gender.type}</span>
                  <span className="text-sm text-primary font-medium">
                    {displayData.demographics.gender.percentage}%
                  </span>
                </div>
              )}
              {displayData.demographics.ageGroup && (
                <div className="flex justify-between items-center py-3 border-b border-border-primary">
                  <span className="text-sm text-primary">Age {displayData.demographics.ageGroup.range}</span>
                  <span className="text-sm text-primary font-medium">
                    {displayData.demographics.ageGroup.percentage}%
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-tertiary italic">Demographics data not available</div>
          )}
        </div>

        {/* Social Stats - No title, just data */}
        <div className="space-y-0">
          {displayData.socialStats.followers > 0 && (
            <div className="flex justify-start gap-3 items-center py-3 border-b border-border-primary">
              <Users01 className="w-4 h-4 text-tertiary" />
              <span className="text-sm text-primary font-medium">
                {numFormatter.format(displayData.socialStats.followers)}
              </span>
              <span className="text-sm text-tertiary">Followers</span>
            </div>
          )}
          {displayData.socialStats.avgLikes > 0 && (
            <div className="flex justify-start gap-3 items-center py-3 border-b border-border-primary">
              <Heart className="w-4 h-4 text-tertiary" />
              <span className="text-sm text-primary font-medium">
                {numFormatter.format(displayData.socialStats.avgLikes)}
              </span>
              <span className="text-sm text-tertiary">Avg. Likes per Video</span>
            </div>
          )}
          {displayData.socialStats.followers === 0 && displayData.socialStats.avgLikes === 0 && (
            <div className="text-sm text-tertiary italic py-3">Social stats not available</div>
          )}
        </div>

        {/* Links */}
        <div className="space-y-3 mt-15">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Share03 className="w-4 h-4 text-tertiary" />
              <h3 className="text-sm font-medium text-secondary">Links</h3>
            </div>
            <div className="h-[1px] bg-border-primary" />
          </div>
          <div className="flex flex-col gap-2 overflow-hidden">
            {displayData.links.tiktokProfile && (
              <a
                href={displayData.links.tiktokProfile}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-brand-primary hover:underline truncate block"
              >
                See TikTok profile →
              </a>
            )}
            {displayData.links.externalWebsite && (
              <a
                href={`https://${displayData.links.externalWebsite}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-brand-primary hover:underline break-all block"
              >
                {displayData.links.externalWebsite} →
              </a>
            )}
            {!displayData.links.tiktokProfile && !displayData.links.externalWebsite && (
              <div className="text-sm text-tertiary italic">No links available</div>
            )}
          </div>
        </div>

        {/* Unbox List - REAL DATA */}
        <div className="space-y-3 mt-15">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <File06 className="w-4 h-4 text-tertiary" />
              <h3 className="text-sm font-medium text-secondary">Unbox List</h3>
            </div>
            <div className="h-[1px] bg-border-primary" />
          </div>
          <div className="flex flex-col gap-2">
            {data.lists && data.lists.length > 0 ? (
              data.lists.map((list) => (
                <div key={list.id} className="text-sm text-brand-primary hover:underline cursor-pointer truncate">
                  {list.name}
                  {list.description && <span className="text-tertiary italic"> - {list.description}</span>}
                </div>
              ))
            ) : (
              <div className="text-sm text-tertiary">Not on any list</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
